import { useEffect, useLayoutEffect, useRef } from "react";
import type { RefObject } from "react";

/** Attribute a row's marker element carries so this hook can find it inside `containerRef`.
 * `RulesResultPanel.tsx` stamps this onto its `itemEnd` render output. */
export const FLIP_ROW_MARKER_ATTRIBUTE = "data-flip-row-id";

/**
 * CSS class the shared `AccordionListPanel` already applies to every row's root element — used to
 * walk up from a row's marker to the element that should actually be transformed, so the whole row
 * slides as one unit. Falls back to the marker itself if no such ancestor exists.
 */
const ROW_ELEMENT_SELECTOR = ".alp-item";

const DEFAULT_DURATION_MS = 200;

export interface UseFlipReorderOptions {
  /** Container that holds every row's marker element (`[data-flip-row-id]`) — a ref, so it survives
   * re-renders without becoming a new dependency identity every time. */
  containerRef: RefObject<HTMLElement>;
  /** Stable ids for the CURRENTLY rendered rows, in their current visual (post-sort) order. */
  rowIds: string[];
  /** Master switch (`RESULTS_ORDERING_MODE === "live"`); `false` makes every run of this hook a
   * complete no-op — no measuring, no style writes. */
  enabled: boolean;
  /** From `usePrefersReducedMotion()`. Re-sorting still happens; rows just jump straight to
   * their new position with no invert/play animation. */
  prefersReducedMotion: boolean;
  /** "Play" transition duration in ms. Defaults to 200. */
  durationMs?: number;
}

function findRowElement(container: Element, id: string): HTMLElement | null {
  // A plain quoted attribute selector is safe here without CSS.escape: row ids are generated
  // internally (see RulesPanel.tsx's `buildPlaceholderRunningRow` / RunController) and never
  // contain a quote or backslash character.
  const marker = container.querySelector<HTMLElement>(`[${FLIP_ROW_MARKER_ATTRIBUTE}="${id}"]`);
  if (!marker) return null;
  return marker.closest<HTMLElement>(ROW_ELEMENT_SELECTOR) ?? marker;
}

/** Order-sensitive array equality for two id sequences (see `hasGenuineReorder`). Plain `===`
 * never applies since the compared arrays are always freshly filtered. */
function sameSequence(a: readonly string[], b: readonly string[]): boolean {
  return a.length === b.length && a.every((id, index) => id === b[index]);
}

/**
 * The actual reorder gate. A plain full-sequence comparison is wrong whenever a row was dismissed
 * or inserted: that changes the full sequence too, even though the rows present in both commits
 * kept their exact relative order. Restricts both sequences down to only the ids present in both
 * commits (preserving each commit's own relative order) and compares those — a pure
 * insertion/removal leaves the restricted comparison unchanged, while a genuine relative-order
 * change among the surviving rows still changes it.
 */
function hasGenuineReorder(previousIds: string[] | null, currentIds: string[]): boolean {
  if (previousIds === null) return false; // no previous commit measured yet — nothing to invert FROM.
  const currentIdSet = new Set(currentIds);
  const previousIdSet = new Set(previousIds);
  const commonPreviousOrder = previousIds.filter((id) => currentIdSet.has(id));
  const commonCurrentOrder = currentIds.filter((id) => previousIdSet.has(id));
  return !sameSequence(commonPreviousOrder, commonCurrentOrder);
}

/**
 * Hand-written FLIP (First-Last-Invert-Play) hook that animates result rows sliding to their new
 * position whenever the `live` ordering mode re-sorts the list, with no animation dependency.
 * Feature-scoped to the rules-run results panel — not a generic/shared utility.
 *
 * Classic FLIP, evaluated in a `useLayoutEffect` on every render:
 *  - First:  each tracked row's rect as of the end of the previous run of this effect (derived
 *            from the previous "Last" measurement, since function components have no
 *            `getSnapshotBeforeUpdate`).
 *  - Last:   each row's rect measured here, synchronously after the DOM updates but before paint.
 *  - Invert: a row whose Y position changed gets an immediate (no-transition) inverse
 *            `translateY`, so for one frame it still visually sits at the old spot.
 *  - Play:   on the next animation frame, the transform is cleared with a transition, animating
 *            the row to its natural resting position.
 *
 * Invert/Play only runs when the row id sequence itself changed since the previous commit — a
 * row's measured Y can differ between commits for reasons that aren't a reorder at all (a sibling
 * card expanding/collapsing, a scrollbar appearing), and treating that as a reorder produced a
 * visible "row translates and snaps back" artifact. Cached rects still refresh on every commit
 * regardless, so a later genuine reorder always inverts from the most recent resting position.
 *
 * The order-change gate (`hasGenuineReorder`) restricts both sequences to only the ids present in
 * both commits before comparing: a pure insertion/removal (e.g. dismissing a row) never triggers an
 * animation for the surviving rows that merely shifted to fill the gap, while a genuine
 * relative-order change among survivors still does — even in the same commit as an insertion or
 * removal.
 *
 * A row only ever animates when present in both the previous and current measurement — one that
 * just appeared or disappeared is left alone with no crash and no stale bookkeeping; enter/exit is
 * kendo-react-animation's job, not this hook's. When `prefersReducedMotion` is true, the list still
 * re-sorts but no row is ever given a transform/transition.
 */
export function useFlipReorder({
  containerRef,
  rowIds,
  enabled,
  prefersReducedMotion,
  durationMs = DEFAULT_DURATION_MS,
}: UseFlipReorderOptions): void {
  const previousRectsRef = useRef<Map<string, DOMRect>>(new Map());
  // The previously-seen rowIds sequence, kept as a raw array (not a joined signature) so
  // hasGenuineReorder can restrict it to ids also present in the current commit. `null` means "no
  // commit measured yet" (mount, or right after a re-enable) — never treated as "order changed".
  const previousIdsRef = useRef<string[] | null>(null);
  const pendingRef = useRef<Map<string, () => void>>(new Map());

  useLayoutEffect(() => {
    const container = containerRef.current;

    const cancelPending = (id: string) => {
      const cancel = pendingRef.current.get(id);
      if (!cancel) return;
      pendingRef.current.delete(id);
      cancel();
    };

    if (!enabled || !container) {
      // Disabled or nothing rendered yet: never measure, never touch a row's style. Drop tracked
      // state so a later re-enable starts fresh.
      Array.from(pendingRef.current.keys()).forEach(cancelPending);
      previousRectsRef.current = new Map();
      previousIdsRef.current = null;
      return;
    }

    const previousRects = previousRectsRef.current;
    const previousIds = previousIdsRef.current;
    previousIdsRef.current = rowIds;

    const nextRects = new Map<string, DOMRect>();
    const rowElements = new Map<string, HTMLElement>();

    rowIds.forEach((id) => {
      const rowEl = findRowElement(container, id);
      if (!rowEl) return;
      rowElements.set(id, rowEl);
      nextRects.set(id, rowEl.getBoundingClientRect());
    });

    // Rows that vanished since the previous commit can never be animated again — drop any leftover
    // pending cleanup so nothing later touches a now-detached node.
    Array.from(previousRects.keys())
      .filter((id) => !nextRects.has(id))
      .forEach(cancelPending);

    // The core gate: only a genuine relative-order change among rows present in both commits is
    // allowed to invert/play; a pure insertion/removal only refreshes the cached rects above.
    const hasOrderChanged = hasGenuineReorder(previousIds, rowIds);

    if (hasOrderChanged && !prefersReducedMotion) {
      rowElements.forEach((rowEl, id) => {
        const previousRect = previousRects.get(id);
        const nextRect = nextRects.get(id);
        if (!previousRect || !nextRect) return; // newly appeared row — nothing to invert FROM.

        const deltaY = previousRect.top - nextRect.top;
        if (deltaY === 0) return; // No visible movement — no-op.

        cancelPending(id);

        // INVERT — jump back to the old spot with no transition; the reorder itself stays invisible.
        rowEl.style.transition = "none";
        rowEl.style.transform = `translateY(${deltaY}px)`;

        const cleanup = () => {
          rowEl.style.transition = "";
          rowEl.style.transform = "";
          rowEl.removeEventListener("transitionend", handleTransitionEnd);
          clearTimeout(timeoutId);
          pendingRef.current.delete(id);
        };
        const handleTransitionEnd = (event: TransitionEvent) => {
          if (event.target === rowEl && event.propertyName === "transform") cleanup();
        };
        rowEl.addEventListener("transitionend", handleTransitionEnd);
        // Fallback in case `transitionend` never fires (jsdom has no real layout/paint engine, and
        // a real browser could hide the row mid-flight) — always converges to a clean resting state.
        const timeoutId = setTimeout(cleanup, durationMs + 50);

        // PLAY — on the next frame, animate to the natural (0) resting position.
        const rafId = requestAnimationFrame(() => {
          rowEl.style.transition = `transform ${durationMs}ms ease`;
          rowEl.style.transform = "";
        });

        pendingRef.current.set(id, () => {
          cancelAnimationFrame(rafId);
          rowEl.removeEventListener("transitionend", handleTransitionEnd);
          clearTimeout(timeoutId);
          rowEl.style.transition = "";
          rowEl.style.transform = "";
        });
      });
    }

    previousRectsRef.current = nextRects;
  }, [containerRef, rowIds, enabled, prefersReducedMotion, durationMs]);

  // Unmount: cancel every still-pending animation so nothing fires (or mutates a detached node)
  // after teardown.
  useEffect(() => {
    return () => {
      pendingRef.current.forEach((cancel) => cancel());
      pendingRef.current.clear();
    };
  }, []);
}
